import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X } from "lucide-react";

interface SearchableSelectProps<T> {
  items: T[];
  value: string;
  onChange: (id: string) => void;
  getId: (it: T) => string;
  getLabel: (it: T) => string;
  getSecondary?: (it: T) => string | undefined | null;
  placeholder?: string;
  emptyText?: string;
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
}

function semAcento(s: string) {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * Combobox simples com busca. Usado para escolher pessoa/projeto/cliente
 * sem precisar abrir dropdown nativo enorme.
 */
export function SearchableSelect<T>({
  items,
  value,
  onChange,
  getId,
  getLabel,
  getSecondary,
  placeholder = "Selecionar…",
  emptyText = "Nenhum resultado",
  required,
  disabled,
  allowClear = true,
  className,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selecionado = useMemo(() => items.find((i) => getId(i) === value) || null, [items, value, getId]);

  const filtrados = useMemo(() => {
    const t = semAcento(query.trim());
    if (!t) return items.slice(0, 50);
    return items
      .filter((i) => {
        const label = semAcento(getLabel(i));
        const sec = getSecondary?.(i) ? semAcento(getSecondary(i)!) : "";
        return label.includes(t) || sec.includes(t);
      })
      .slice(0, 50);
  }, [items, query, getLabel, getSecondary]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // foco no input após abrir
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  function escolher(it: T) {
    onChange(getId(it));
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setOpen(false);
      e.preventDefault();
    } else if (e.key === "ArrowDown") {
      setActiveIdx((i) => Math.min(i + 1, filtrados.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      const it = filtrados[activeIdx];
      if (it) escolher(it);
      e.preventDefault();
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className || ""}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left outline-none focus:border-blue-500 disabled:opacity-50 ${
          required && !value ? "border-slate-300" : "border-slate-300"
        } bg-white`}
      >
        <span className={`flex-1 truncate ${selecionado ? "text-slate-800" : "text-slate-400"}`}>
          {selecionado ? getLabel(selecionado) : placeholder}
        </span>
        {allowClear && selecionado && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-slate-400 hover:text-slate-600 shrink-0"
            title="Limpar"
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
          <div className="relative border-b border-slate-100">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKeyDown}
              placeholder="Digite para buscar…"
              className="w-full pl-9 pr-3 py-2 text-sm outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtrados.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-400 text-center">{emptyText}</p>
            )}
            {filtrados.map((it, i) => {
              const id = getId(it);
              const ativo = id === value;
              const hover = i === activeIdx;
              return (
                <button
                  key={id}
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => escolher(it)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                    ativo ? "bg-blue-50 text-blue-700 font-medium" : hover ? "bg-slate-50" : ""
                  }`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{getLabel(it)}</span>
                    {getSecondary?.(it) && (
                      <span className="block text-[11px] text-slate-400 truncate">{getSecondary(it)}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
